import React, { useEffect, useState, useCallback } from "react";
import { FiMenu, FiX } from "react-icons/fi";
import { Icon } from "@mdi/react";
import {
  mdiInformation,
  mdiHandshake,
  mdiPhone,
  mdiCog,
  mdiBriefcase,
  mdiHome
} from "@mdi/js";
import logo from "../assets/logo.png";
import { useNavigate } from "react-router-dom";

const Header = ({ scrollToSection }) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const handleScroll = useCallback(() => {
    requestAnimationFrame(() => {
      setScrollPosition(window.scrollY);
    });
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const headerElevation = Math.min(scrollPosition / 10, 15);
  const logoScale = scrollPosition > 50 ? 0.9 : 1;

  const navItems = [
    { label: "About Us", path: "/abouts", icon: mdiInformation },
    { label: "Services", path: "/services", icon: mdiCog },
    { label: "What Client Say", path: "/clients", icon: mdiHandshake },
    { label: "Get In Touch", path: "/contact", icon: mdiPhone },
    { label: "My Projects", path: "/projects", icon: mdiBriefcase },
    { label: "Home", path: "/", icon: mdiHome },
  ];
  
  

  const handleNavigation = (e, path) => {
    e.preventDefault();
    setSidebarOpen(false);
    navigate(path);
  };

  return (
    <>
      <header
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: "#FFFFFF",
          zIndex: 1000,
          padding: "10px 15px",
          height: "70px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          transition: "all 0.3s ease",
          width: "100%",
          boxShadow: `0 ${headerElevation / 3}px ${headerElevation}px rgba(0, 45, 85, 0.1)`,
        }}
      >
        <div className="logo-container">
          <div
            className="logo-circle"
            style={{ transform: `scale(${logoScale})`, transition: "transform 0.3s ease" }}
          >
            <img src={logo} alt="Logo" className="logo" />
          </div>
        </div>

        <div className="mobile-menu-icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <FiX size={28} color="#002d55" /> : <FiMenu size={28} color="#002d55" />}
        </div>

        <nav className="desktop-nav">
          <ul className="nav-list">
            {navItems.map(({ label, path, icon }) => (
              <li key={label}>
                <a href={path} onClick={(e) => handleNavigation(e, path)} className="nav-link">
                  <Icon path={icon} size={0.8} style={{ marginRight: "5px" }} />
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      {/* Sidebar for Mobile */}
      <div className={`mobile-sidebar ${sidebarOpen ? "open" : ""}`}>
        {navItems.map(({ label, path, icon }) => (
          <a
            key={label}
            href={path}
            onClick={(e) => handleNavigation(e, path)}
            className="mobile-nav-link"
          >
            <Icon path={icon} size={0.9} style={{ marginRight: "5px" }} />
            {label}
          </a>
        ))}
      </div>

      <style>{`
        .logo-container {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .logo-circle {
          background-color: #002d55;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          box-shadow: 0 4px 8px rgba(0, 45, 85, 0.2);
        }

        .logo {
          height: 40px;
          width: auto;
          object-fit: contain;
        }

        .nav-list {
          display: flex;
          list-style: none;
          gap: 24px;
          padding: 0;
          margin: 0;
        }

        .nav-link {
          color: #002d55;
          text-decoration: none;
          font-weight: bold;
          font-size: 18px;
          position: relative;
          padding: 5px 2px;
          transition: color 0.3s;
        }

        .nav-link::after {
          content: '';
          position: absolute;
          width: 0;
          height: 3px;
          bottom: -2px;
          left: 0;
          background-color: #002d55;
          transition: width 0.3s ease;
        }

        .nav-link:hover {
          color: #0056b3;
        }

        .nav-link:hover::after {
          width: 100%;
        }

        .desktop-nav {
          display: flex;
        }

        .mobile-menu-icon {
          display: none;
          cursor: pointer;
          transition: transform 0.3s ease;
        }

        .mobile-menu-icon:hover {
          transform: rotate(180deg);
        }

        .mobile-sidebar {
          position: fixed;
          top: 70px;
          right: -100%;
          width: 70%;
          height: calc(100vh - 70px);
          background-color: white;
          display: flex;
          flex-direction: column;
          align-items: start;
          justify-content: flex-start;
          padding: 20px;
          gap: 20px;
          transition: right 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55);
          z-index: 999;
          box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
        }

        .mobile-sidebar.open {
          right: 0;
        }

        .mobile-nav-link {
          color: #002d55;
          text-decoration: none;
          font-size: 20px;
          font-weight: bold;
          padding: 10px 0;
          width: 100%;
          border-bottom: 1px solid #eaeaea;
          transition: transform 0.3s ease, color 0.3s ease;
        }

        .mobile-nav-link:hover {
          color: #0056b3;
          transform: translateX(10px);
        }

        @media (max-width: 768px) {
          .desktop-nav {
            display: none;
          }

          .mobile-menu-icon {
            display: block;
            margin-right: 25px;
          }

          .nav-link {
            font-size: 13px !important;
          }

          header {
            justify-content: space-between;
            padding-right: 15px;
          }
        }
      `}</style>
    </>
  );
};

export default Header;
